// Pure parser for the accounting section of a hash route (drill-through
// wave B). App.tsx has TWO call sites that react to
// `#/accounting/<sub>[?params]` — a `hashchange` listener and the
// initial-mount parse — both of which previously read only `seg[1]` for the
// sub-view and never looked at a query string at all. This is the SINGLE
// shared parser both sites now call, so a deep link like
// `#/accounting/journal-entries?je=42` behaves identically whether the tab
// was already open (hashchange) or the page was freshly loaded (mount).
//
// Input: the raw hash PATH after `#/` — i.e. what
// `window.location.hash.replace(/^#\/?/, '')` produces. Examples:
//   'accounting/accounts-receivable?invoice=INV-123'
//   'accounting'
//   'accounting/journal-entries?je=42&search=foo'
//   'contacts/record/9'                                (non-accounting)
// A leading '#/' or '#' is tolerated defensively even though callers are
// expected to have already stripped it (getHashSegments()-style usage).

export interface ParsedAccountingHash {
  /** The sub-view segment (`seg[1]`). Null when the path's first segment
   *  isn't 'accounting', OR it IS 'accounting' but no sub-segment follows
   *  (caller falls back to a default, e.g. 'overview' — same as before this
   *  helper existed). Segments past the sub-view (seg[2], seg[3], ...) are
   *  ignored, same as the original seg[1]-only reads. */
  sub: string | null
  /** Query params as a plain object, URL-decoded. Always an object — empty
   *  when there's no '?', an empty '?', or the path isn't 'accounting'.
   *  A malformed/extra '?' (e.g. 'a=1?b=2') is tolerated, never throws. */
  params: Record<string, string>
}

export function parseAccountingHash(hashPath: string): ParsedAccountingHash {
  const raw = String(hashPath || '').replace(/^#\/?/, '')
  const qIdx = raw.indexOf('?')
  const pathPart = qIdx >= 0 ? raw.slice(0, qIdx) : raw
  const queryPart = qIdx >= 0 ? raw.slice(qIdx + 1) : ''
  const segs = pathPart.split('/').filter(Boolean)

  if (segs[0] !== 'accounting') {
    return { sub: null, params: {} }
  }

  const sub = segs[1] || null
  const params: Record<string, string> = {}
  if (queryPart) {
    // URLSearchParams never throws on a malformed string — a literal extra
    // '?' (e.g. 'a=1?b=2') just becomes part of the first pair's value.
    const usp = new URLSearchParams(queryPart)
    usp.forEach((value, key) => { params[key] = value })
  }
  return { sub, params }
}
